import type { AgentRequirement, Device } from "./types";

/**
 * Agent versions, as the server judges them (app/core/agent_versions.py): `1.1.0`,
 * `1.1.0-dev` and `v1.1` are the same version; trailing zeros don't count.
 */
export function parseVersion(text: string | null | undefined): number[] | null {
  const match = text?.match(/\d+(?:\.\d+)*/);
  if (!match) return null;
  const parts = match[0].split(".").map(Number);
  while (parts.length > 1 && parts.at(-1) === 0) parts.pop();
  return parts;
}

/** Whether [version] is known to be older than [minimum]. A device that never reported
 * its version is not second-guessed. */
export function isOlder(version: string | null | undefined, minimum: string): boolean {
  const have = parseVersion(version);
  const need = parseVersion(minimum);
  if (!have || !need) return false;
  for (let i = 0; i < Math.max(have.length, need.length); i++) {
    const a = have[i] ?? 0;
    const b = need[i] ?? 0;
    if (a !== b) return a < b;
  }
  return false;
}

export type Requirements = Record<string, AgentRequirement>;

/** Why the device's agent is too old for [feature], or null. */
export function versionReason(
  feature: string,
  device: Pick<Device, "agent_version">,
  requirements: Requirements | undefined,
): string | null {
  const requirement = requirements?.[feature];
  if (!requirement || !isOlder(device.agent_version, requirement.min_version)) return null;
  return `Needs agent ${requirement.min_version} or newer; this device runs ${device.agent_version}`;
}
